// Receipts (PRD 21): photographed or uploaded, read, and matched to the bank
// transaction each explains. Every write answers with the whole receipt, and
// every write parses its response, so a refusal throws with the server's reason.

import { businessApiGet, businessApiPatch, businessApiPost, businessApiPostForm } from '../utils/apiUtils';
import { buildQuery, parseData, parseEnvelope } from './businessApiCommon';
import {
  BusinessReceipt,
  DocumentUploadResult,
  ReceiptNoMatchReason,
  ReceiptPage,
  ReceiptReadingInput,
  ReceiptView,
} from '../types/businessApi';

const BASE = '/api/v1/business/receipts';

export interface ReceiptCaptureResult {
  receipt: BusinessReceipt;
  duplicateOf: DocumentUploadResult['duplicateOf'];
}

export const businessReceiptService = {
  async list(view: ReceiptView = 'open', cursor?: string): Promise<ReceiptPage> {
    const { data, meta } = await parseEnvelope<BusinessReceipt[]>(
      await businessApiGet(`${BASE}${buildQuery({ view, cursor })}`)
    );
    return {
      receipts: data,
      nextCursor: (meta.next_cursor as string | null | undefined) ?? null,
      openCount: typeof meta.open_count === 'number' ? meta.open_count : 0,
      canWrite: meta.can_write === true,
      monthCount: typeof meta.month_count === 'number' ? meta.month_count : null,
      storageBytes: typeof meta.storage_bytes === 'number' ? meta.storage_bytes : null,
    };
  },

  async get(id: string): Promise<BusinessReceipt> {
    return parseData<BusinessReceipt>(await businessApiGet(`${BASE}/${id}`));
  },

  /**
   * A photo or a PDF. Read in the background; the receipt comes back waiting.
   * `thumbnail` is the small picture drawThumbnail made of it, when it could.
   */
  async capture(file: File, thumbnail?: Blob | null): Promise<ReceiptCaptureResult> {
    const form = new FormData();
    form.append('file', file);
    if (thumbnail) form.append('thumbnail', thumbnail, 'thumbnail.jpg');
    const { data, meta } = await parseEnvelope<BusinessReceipt>(await businessApiPostForm(BASE, form));
    return { receipt: data, duplicateOf: (meta.duplicate_of as ReceiptCaptureResult['duplicateOf']) ?? null };
  },

  async confirm(id: string): Promise<BusinessReceipt> {
    return parseData<BusinessReceipt>(await businessApiPost(`${BASE}/${id}/confirm`));
  },

  /** "Not this one": never suggested for this receipt again; the next best is. */
  async reject(id: string): Promise<BusinessReceipt> {
    return parseData<BusinessReceipt>(await businessApiPost(`${BASE}/${id}/reject`));
  },

  async link(id: string, bankTransactionId: string): Promise<BusinessReceipt> {
    return parseData<BusinessReceipt>(
      await businessApiPost(`${BASE}/${id}/link`, { bank_transaction_id: bankTransactionId })
    );
  },

  async noMatch(id: string, reason: ReceiptNoMatchReason): Promise<BusinessReceipt> {
    return parseData<BusinessReceipt>(await businessApiPost(`${BASE}/${id}/no-match`, { reason }));
  },

  /** What the receipt says, as a person reads it; matched again with it. */
  async correct(id: string, input: ReceiptReadingInput): Promise<BusinessReceipt> {
    return parseData<BusinessReceipt>(await businessApiPatch(`${BASE}/${id}/reading`, input));
  },

  async readAgain(id: string): Promise<BusinessReceipt> {
    return parseData<BusinessReceipt>(await businessApiPost(`${BASE}/${id}/read-again`));
  },
};

export default businessReceiptService;
